import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Table, Button, Input, Select, Space, Tag, Tooltip,
  Popconfirm, message, Card, Row, Col, Modal, Form
} from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined, RedoOutlined } from "@ant-design/icons";
import { api } from "@redux/api";
import { useSelector } from "react-redux";
import { useEventBus, EventName } from "@/hooks/useEventBus";
import moment from "moment";
import { buildFacilityPath, getFacilityContext } from "@/utils/route-context";

const { Option } = Select;

const PMChecklist = () => {
  const navigate = useNavigate();
  const facilityContext = useSelector((state) => getFacilityContext(state));
  const detailPath = (id) => buildFacilityPath(`pm-checklist-detail/${id}`, facilityContext) || "/switch-view";
  const createPath = buildFacilityPath("create-pm-checklist", facilityContext) || "/switch-view";

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [searchText, setSearchText] = useState("");
  const [categoryFilter, setCategoryFilter] = useState(undefined);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [ordering, setOrdering] = useState("-created_date");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page,
        page_size: pageSize,
      });
      if (searchText) params.append("search", searchText);
      if (categoryFilter) params.append("equipment_category", categoryFilter);
      if (ordering) params.append("ordering", ordering);

      const res = await api.filterPMChecklist(params.toString());
      if (res.status === 200) {
        setData(res.data.results || []);
        setTotal(res.data.count || 0);
      }
    } catch (err) {
      message.error("Failed to load PM Checklists");
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, searchText, categoryFilter, ordering]);

  const fetchCategories = useCallback(async () => {
    try {
      const res = await api.filterEquipmentCategory("page=1&page_size=999");
      if (res.status === 200) {
        setCategoryOptions(res.data.results || []);
      }
    } catch {}
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  useEventBus(EventName.REFRESH_PM_CHECKLIST_LIST, fetchData);

  const handleDelete = async (id) => {
    try {
      await api.deletePMChecklist(id);
      message.success("Deleted successfully");
      fetchData();
    } catch {
      message.error("Failed to delete");
    }
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      sorter: true,
      sortOrder: ordering === "name" ? "ascend" : ordering === "-name" ? "descend" : null,
      render: (text, record) => (
        <a onClick={() => navigate(detailPath(record.id))}>{text}</a>
      ),
    },
    {
      title: "Equipment Category",
      dataIndex: "equipment_category_name",
      key: "equipment_category_name",
      render: (text) => text ? <Tag color="blue">{text}</Tag> : "-",
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      ellipsis: true,
      render: (text) => (
        <Tooltip title={text}>
          <span style={{ maxWidth: 200, display: "inline-block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {text || "-"}
          </span>
        </Tooltip>
      ),
    },
    {
      title: "Fields Count",
      dataIndex: "fields",
      key: "fields_count",
      width: 100,
      align: "center",
      render: (fields) => (
        <Tag color="green">{Array.isArray(fields) ? fields.length : 0} items</Tag>
      ),
    },
    {
      title: "Created Date",
      dataIndex: "created_date",
      key: "created_date",
      width: 120,
      sorter: true,
      sortOrder: ordering === "created_date" ? "ascend" : ordering === "-created_date" ? "descend" : null,
      render: (text) => text ? moment(text).format("YYYY-MM-DD") : "-",
    },
    {
      title: "Actions",
      key: "actions",
      width: 100,
      fixed: "right",
      render: (_, record) => (
        <Space>
          <Tooltip title="Edit">
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => navigate(detailPath(record.id))}
            />
          </Tooltip>
          <Popconfirm
            title="Are you sure to delete this checklist?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Tooltip title="Delete">
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: "16px" }}>
      <Card
        title={
          <span style={{ fontSize: 18, fontWeight: 600 }}>
            <i className="fas fa-clipboard-list" style={{ marginRight: 8, color: "#3A3F63" }} />
            PM Checklists
          </span>
        }
        extra={
          <Space>
            <Tooltip title="Refresh">
              <Button icon={<RedoOutlined />} onClick={fetchData} loading={loading} />
            </Tooltip>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate(createPath)}
            >
              New Checklist
            </Button>
          </Space>
        }
      >
        <Row gutter={12} style={{ marginBottom: 16 }}>
          <Col span={8}>
            <Input
              placeholder="Search by name..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => { setSearchText(e.target.value); setPage(1); }}
              allowClear
            />
          </Col>
          <Col span={6}>
            <Select
              placeholder="Filter by Equipment Category"
              style={{ width: "100%" }}
              allowClear
              value={categoryFilter}
              onChange={(v) => { setCategoryFilter(v); setPage(1); }}
            >
              {categoryOptions.map((cat) => (
                <Option key={cat.id} value={cat.id}>{cat.name}</Option>
              ))}
            </Select>
          </Col>
        </Row>

        <Table
          rowKey="id"
          columns={columns}
          dataSource={data}
          loading={loading}
          scroll={{ x: 800 }}
          onChange={(_, __, sorter, extra) => {
            if (extra?.action === "sort" && sorter?.field) {
              const dir = sorter.order === "ascend" ? "" : "-";
              setOrdering(`${dir}${sorter.field}`);
              setPage(1);
            }
          }}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            showTotal: (t) => `Total ${t} records`,
            onChange: (p, ps) => { setPage(p); setPageSize(ps); },
          }}
        />
      </Card>
    </div>
  );
};

export default PMChecklist;
